/* Peacock Life — Luxe motion engine.
   Content visibility (reveals, counters, scrollytelling) runs on plain scroll handlers plus an
   IntersectionObserver, so it can never get stuck when requestAnimationFrame is throttled or a
   visitor jump-scrolls. Only parallax uses rAF. */
(function () {
  'use strict';

  if (window.__luxeMotion) return;
  window.__luxeMotion = true;
  document.documentElement.classList.add('lx-motion');

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.documentElement;

  var pending = [], counters = [], tracked = [], scrollies = [], io = null;

  function splitWords(el) {
    if (el.dataset.splitDone) return;
    el.dataset.splitDone = '1';
    var words = el.textContent.trim().split(/\s+/);
    if (!words.length || words.length > 24) return;
    el.textContent = '';
    words.forEach(function (w, i) {
      var outer = document.createElement('span');
      outer.className = 'lx-w';
      var inner = document.createElement('span');
      inner.className = 'lx-w__i';
      inner.textContent = w;
      inner.style.transitionDelay = (i * 55) + 'ms';
      outer.appendChild(inner);
      el.appendChild(outer);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
    });
  }

  function show(el) { el.classList.add('is-in'); }

  function collect() {
    var fresh = document.querySelectorAll('[data-reveal]:not([data-reveal-init])');
    if (fresh.length && !io && !REDUCED && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        var hit = false;
        entries.forEach(function (e) {
          if (e.isIntersecting) { show(e.target); io.unobserve(e.target); hit = true; }
        });
        if (hit) sweep(true);
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.01 });
    }

    fresh.forEach(function (el) {
      el.setAttribute('data-reveal-init', '');
      if (el.hasAttribute('data-split')) splitWords(el);
      var group = el.closest('[data-reveal-group]');
      if (group) {
        var sibs = Array.prototype.slice.call(group.querySelectorAll('[data-reveal]'));
        el.style.setProperty('--d', Math.min(sibs.indexOf(el), 8) * 90 + 'ms');
      }
      if (REDUCED) { show(el); return; }
      pending.push(el);
      if (io) io.observe(el);
    });

    tracked = Array.prototype.slice.call(document.querySelectorAll('[data-track]'));

    document.querySelectorAll('[data-count]:not([data-count-init])').forEach(function (el) {
      el.setAttribute('data-count-init', '');
      if (el.closest('form')) return; /* app forms (e.g. Hulk) use data-count for their own fields */
      var target = parseFloat(el.getAttribute('data-count'));
      if (isNaN(target)) return;
      var suffix = el.getAttribute('data-count-suffix') || '';
      if (REDUCED) { el.textContent = target.toLocaleString('en-IN') + suffix; return; }
      counters.push({ el: el, to: target, suffix: suffix });
    });

    document.querySelectorAll('[data-scrolly]:not([data-scrolly-init])').forEach(function (wrap) {
      wrap.setAttribute('data-scrolly-init', '');
      var steps = Array.prototype.slice.call(wrap.querySelectorAll('[data-step]'));
      var frames = Array.prototype.slice.call(wrap.querySelectorAll('[data-frame]'));
      if (!steps.length || !frames.length) return;
      scrollies.push({ wrap: wrap, steps: steps, frames: frames, active: -1 });
    });
  }

  function runCounter(c) {
    var final = c.to.toLocaleString('en-IN') + c.suffix;
    if (!window.requestAnimationFrame) { c.el.textContent = final; return; }
    var start = null, dur = 1500;
    function step(ts) {
      if (!start) start = ts;
      var t = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - t, 4);
      c.el.textContent = Math.round(c.to * eased).toLocaleString('en-IN') + c.suffix;
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
    setTimeout(function () { c.el.textContent = final; }, dur + 500);
  }

  var lastSweep = 0;
  function sweep(force) {
    var now = Date.now();
    if (!force && now - lastSweep < 60) return;
    lastSweep = now;

    var vh = window.innerHeight || 1;
    var max = root.scrollHeight - vh;
    root.style.setProperty('--scrollp', max > 0 ? (window.scrollY / max).toFixed(4) : '0');

    if (pending.length) {
      var still = [];
      for (var i = 0; i < pending.length; i++) {
        var el = pending[i];
        var r = el.getBoundingClientRect();
        var invisible = r.width === 0 && r.height === 0;
        if (!invisible && r.top < vh * 0.9) { show(el); if (io) io.unobserve(el); }
        else { still.push(el); }
      }
      pending = still;
    }

    if (counters.length) {
      var left = [];
      for (var c = 0; c < counters.length; c++) {
        var cr = counters[c].el.getBoundingClientRect();
        if (cr.top < vh * 0.88 && cr.bottom > -vh) runCounter(counters[c]);
        else left.push(counters[c]);
      }
      counters = left;
    }

    for (var s = 0; s < scrollies.length; s++) {
      var sc = scrollies[s];
      /* a section can move the trigger line with --lx-mid (e.g. below a pinned photo on phones) */
      var mid = vh * (parseFloat(getComputedStyle(sc.wrap).getPropertyValue('--lx-mid')) || 0.5), best = 0, bestD = Infinity;
      for (var k = 0; k < sc.steps.length; k++) {
        var sr = sc.steps[k].getBoundingClientRect();
        var d = Math.abs(sr.top + sr.height / 2 - mid);
        if (d < bestD) { bestD = d; best = k; }
      }
      if (best !== sc.active) {
        sc.active = best;
        for (var m = 0; m < sc.steps.length; m++) sc.steps[m].classList.toggle('is-active', m === best);
        for (var f = 0; f < sc.frames.length; f++) sc.frames[f].classList.toggle('is-active', f === best);
      }
    }
  }

  var ticking = false;
  function raf() {
    ticking = false;
    if (REDUCED || !tracked.length) return;
    var vh = window.innerHeight || 1;
    for (var t = 0; t < tracked.length; t++) {
      var te = tracked[t];
      var tr = te.getBoundingClientRect();
      if (tr.bottom < -240 || tr.top > vh + 240) continue;
      var p = (vh - tr.top) / (vh + tr.height);
      if (p < 0) p = 0; else if (p > 1) p = 1;
      te.style.setProperty('--p', p.toFixed(4));
    }
  }

  function onScroll() {
    sweep();
    if (!ticking && window.requestAnimationFrame) { ticking = true; requestAnimationFrame(raf); }
  }

  function initMagnetic() {
    if (REDUCED || window.matchMedia('(pointer: coarse)').matches) return;
    document.querySelectorAll('[data-magnetic]:not([data-mag-init])').forEach(function (el) {
      el.setAttribute('data-mag-init', '');
      el.addEventListener('mousemove', function (ev) {
        var r = el.getBoundingClientRect();
        var x = (ev.clientX - r.left - r.width / 2) / r.width;
        var y = (ev.clientY - r.top - r.height / 2) / r.height;
        el.style.transform = 'translate3d(' + (x * 6).toFixed(2) + 'px,' + (y * 4).toFixed(2) + 'px,0)';
      });
      el.addEventListener('mouseleave', function () { el.style.transform = ''; });
    });
  }

  function boot() { collect(); initMagnetic(); sweep(true); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () { sweep(true); }, { passive: true });
  window.addEventListener('load', function () { boot(); sweep(true); });
  window.addEventListener('pageshow', function () { sweep(true); });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) sweep(true); });
  document.addEventListener('shopify:section:load', boot);

  setTimeout(function () { sweep(true); }, 800);
  setTimeout(function () { sweep(true); }, 2500);
})();
